import { useEffect, useRef } from "react";

// Zones à éviter : tout élément marqué `data-particles-avoid` (textes,
// composeur, messages...). Les particules s'y effacent et aucun trait ne les
// traverse, pour ne jamais passer par-dessus le contenu.
const AVOID_PAD = 10;

function segHitsRect(x1, y1, x2, y2, r) {
  // Liang–Barsky : le segment coupe-t-il le rectangle (agrandi) ?
  const l = r.left - AVOID_PAD, t = r.top - AVOID_PAD, rr = r.right + AVOID_PAD, b = r.bottom + AVOID_PAD;
  let t0 = 0, t1 = 1;
  const dx = x2 - x1, dy = y2 - y1;
  const p = [-dx, dx, -dy, dy];
  const q = [x1 - l, rr - x1, y1 - t, b - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return false; continue; }
    const v = q[i] / p[i];
    if (p[i] < 0) { if (v > t1) return false; if (v > t0) t0 = v; }
    else { if (v < t0) return false; if (v < t1) t1 = v; }
  }
  return true;
}

export default function ParticlesBg() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let mouse = { x: -1000, y: -1000 };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const particles = [];
    const count = Math.min(45, Math.floor((canvas.width * canvas.height) / 25000));

    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.05,
        c: ["#5865F2","#7289da","#ffffff","#4752C4"][Math.floor(Math.random() * 4)],
      });
    }

    let zones = [];
    let zonesAt = 0;
    const readZones = () => {
      zones = [];
      document.querySelectorAll("[data-particles-avoid]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < canvas.height) zones.push(r);
      });
    };
    const inZone = (x, y) => zones.some((r) => x > r.left - AVOID_PAD && x < r.right + AVOID_PAD && y > r.top - AVOID_PAD && y < r.bottom + AVOID_PAD);
    const crossesZone = (x1, y1, x2, y2) => zones.length > 0 && zones.some((r) => segHitsRect(x1, y1, x2, y2, r));

    const draw = (now = 0) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width;
      const h = canvas.height;
      if (now - zonesAt > 200) { readZones(); zonesAt = now; }
      // visibilité de chaque particule : s'efface doucement dans une zone
      for (const p of particles) {
        const target = inZone(p.x, p.y) ? 0 : 1;
        p.a = p.a === undefined ? target : p.a + (target - p.a) * 0.18;
      }

      // Lines between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = dx * dx + dy * dy;
          if (d < 22500) { // 150*150
            const a = Math.min(particles[i].a, particles[j].a);
            if (a < 0.02 || crossesZone(particles[i].x, particles[i].y, particles[j].x, particles[j].y)) continue;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(88,101,242,${0.06 * a * (1 - Math.sqrt(d) / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        // Line to mouse
        const mdx = particles[i].x - mouse.x;
        const mdy = particles[i].y - mouse.y;
        const md = mdx * mdx + mdy * mdy;
        if (md < 32400 && particles[i].a > 0.02 && !crossesZone(particles[i].x, particles[i].y, mouse.x, mouse.y)) { // 180*180
          ctx.beginPath();
          ctx.strokeStyle = `rgba(255,255,255,${0.12 * particles[i].a * (1 - Math.sqrt(md) / 180)})`;
          ctx.lineWidth = 0.6;
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }

      // Draw particles
      for (const p of particles) {
        if (p.a > 0.02) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, 6.283);
          ctx.fillStyle = p.c;
          ctx.globalAlpha = p.o * p.a;
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    const onMouse = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onClick = (e) => {
      for (let i = 0; i < 2; i++) {
        particles.push({
          x: e.clientX + (Math.random() - 0.5) * 15,
          y: e.clientY + (Math.random() - 0.5) * 15,
          vx: (Math.random() - 0.5) * 1.2,
          vy: (Math.random() - 0.5) * 1.2,
          r: Math.random() * 1.5 + 0.8,
          o: 0.35,
          c: "#ffffff",
        });
      }
      while (particles.length > count + 20) particles.shift();
    };

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouse);
    window.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1,
        pointerEvents: "none",
      }}
    />
  );
}
